import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockTypes } from '../../../../models';
import { Select } from '../../../Select';

import classes from './Links.module.scss';

interface LinksProps {
  editor: Editor;
}

export const Links: FunctionComponent<LinksProps> = ({ editor }) => {
  const { t } = useTranslation();

  const {
    isActiveUser,
    isActiveStory,
    isActiveStoryCard,
    isActiveEmbedLink,
    isActiveParagraph,
    isActiveList,
    isActiveTable,
  } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isActiveUser: editor.isActive('userNode'),
      isActiveStory: editor.isActive('storyNode'),
      isActiveStoryCard: editor.isActive('storyCardNode'),
      isActiveEmbedLink: editor.isActive('embedLinkNode'),
      isActiveParagraph: editor.isActive('paragraph'),
      isActiveList:
        editor.isActive('taskList') ||
        editor.isActive('orderedList') ||
        editor.isActive('bulletList'),
      isActiveTable: editor.isActive('table'),
    }),
  });

  const items = useMemo(
    () => [
      { id: BlockTypes.user, title: t('richTextEditor.tool-names-user') },
      { id: BlockTypes.story, title: t('richTextEditor.tool-names-story') },
      { id: BlockTypes.card, title: t('richTextEditor.tool-names-story-card') },
      { id: BlockTypes.embedlink, title: t('richTextEditor.tool-names-embed-link') },
    ],
    [t]
  );

  const onChange = useCallback(
    (type: BlockTypes) => {
      switch (type) {
        case BlockTypes.user:
          return editor.commands.insertUserNode({ user: null });
        case BlockTypes.story:
          return editor.commands.insertStoryNode({ story: null });
        case BlockTypes.card:
          return editor.commands.insertStoryCardNode({ storyCard: null });
        case BlockTypes.embedlink:
          return editor.commands.insertEmbedLinkNode({ link: null });
      }
    },
    [editor]
  );

  const selectedItemId = isActiveUser
    ? BlockTypes.user
    : isActiveStory
    ? BlockTypes.story
    : isActiveStoryCard
    ? BlockTypes.card
    : isActiveEmbedLink
    ? BlockTypes.embedlink
    : undefined;

  return (
    <Select
      className={classes['links']}
      placeholderLabel={t('common.link')}
      selectedItemId={selectedItemId}
      active={Boolean(selectedItemId)}
      items={items}
      onChange={(type) => onChange(type as BlockTypes)}
      disabled={!isActiveParagraph || isActiveList || isActiveTable}
      noBorder
    />
  );
};
